import { useCallback, useEffect, useState } from 'react';
import { SxStyle } from '../../../../types';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';
import Typography from '@mui/material/Typography';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  CircularProgress,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import StaffMemberSelect from './staff-member-select';
import { Check } from '@mui/icons-material';
import { BaseDialogProps } from '../../../common/base-dialog';
import { StaffMemberInternal } from '../types';
import { useStringsContext } from '../../../../contexts/strings';
import { useStaff } from '../hooks/staff';
import SquadDialog from '../../squad-database/components/squad-dialog';
import PlayerAvatar from '../../../common/player-avatar';
import ImageDropzone, {
  FileRejectionWithDimensions,
  FileWithDimensions,
} from '../../../common/image-dropzone';

const MIN_IMAGE_DIMENSION = 800;
const MIN_IMAGE_ERROR = 'image-too-small';

interface Props extends Pick<BaseDialogProps, 'open' | 'onClose'> {
  onSubmitted: () => void;
  staff: StaffMemberInternal[];
  teamId: string;
  season: string;
}

const styles: SxStyle = {
  subTitle: {
    paddingLeft: '24px',
    paddingRight: '24px',
    paddingBottom: '16px',
  },
  contentList: {
    padding: '16px',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
  },
  tableRow: (theme) => ({
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
      border: 0,
    },
  }),
  dropzone: {
    minHeight: '200px',
    padding: '20px',
  },
  dropzoneText: {
    fontSize: '1rem',
  },
};

function imageSizeValidator(file: FileWithDimensions) {
  if (file.width < MIN_IMAGE_DIMENSION || file.height < MIN_IMAGE_DIMENSION) {
    return {
      code: MIN_IMAGE_ERROR,
      message: `Bilden är för liten (${file.width}x${file.height} px)`,
    };
  }
  return null;
}

export default function UploadStaffImagesModal({
  open,
  onClose,
  onSubmitted,
  staff,
  teamId,
  season,
}: Props) {
  const { strings } = useStringsContext();
  const { uploadStaffMemberImage } = useStaff();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);

  const [imageMapping, setImageMapping] = useState<
    {
      member: StaffMemberInternal | null;
      file: File;
      status: string | null;
      error?: string;
    }[]
  >([]);

  useEffect(() => {
    setSubmitDisabled(
      imageMapping.every((m) => !m.member || m.status) ||
        imageMapping.some(
          (m) => m.member && imageMapping.filter((m2) => m2.member === m.member).length > 1
        )
    );
  }, [imageMapping]);

  const onSubmit = useCallback(async () => {
    setSubmitLoading(true);
    try {
      let success = false;
      for (const { member, file, status, error } of imageMapping) {
        if (member && file && !status && !error) {
          setImageMapping((prev) =>
            prev.map((m) => (m.member === member ? { ...m, status: 'uploading' } : m))
          );
          try {
            if (!member.id) {
              throw new Error(strings.messages.error);
            }

            await uploadStaffMemberImage(teamId, season, member.id, file);
            success = true;
            setImageMapping((prev) =>
              prev.map((m) => (m.member === member ? { ...m, status: 'uploaded' } : m))
            );
          } catch (e) {
            setImageMapping((prev) =>
              prev.map((m) =>
                m.member === member ? { ...m, status: 'error', error: strings.messages.error } : m
              )
            );
          }
        }
      }
      if (success) {
        onSubmitted?.();
      }
    } finally {
      setSubmitLoading(false);
    }
  }, [imageMapping, onSubmitted, uploadStaffMemberImage, strings, teamId, season]);

  const onImageAdded = useCallback(
    (files: FileWithDimensions[]) => {
      setImageMapping((prev) => [
        ...prev,
        ...files.map((file) => {
          const image = file;
          return {
            member: staff.find((member) => image.name.includes(member.clId!)) || null,
            file: image,
            status: null,
            error: undefined,
          };
        }),
      ]);
    },
    [setImageMapping, staff]
  );

  const onImageRejected = useCallback(
    (files: FileRejectionWithDimensions[]) => {
      setImageMapping((prev) => [
        ...prev,
        ...files.map((file) => {
          const image = file.file;
          return {
            member: staff.find((member) => image.name.includes(member.clId!)) || null,
            file: image,
            status: file.errors?.[0].code === MIN_IMAGE_ERROR ? 'error' : null,
            error: file.errors?.[0].message || undefined,
          };
        }),
      ]);
    },
    [setImageMapping, staff]
  );

  const onRemoveImage = useCallback(
    (index: number) => {
      setImageMapping((prev) => prev.filter((m, i) => index !== i));
    },
    [setImageMapping]
  );

  return (
    <SquadDialog open={open} onClose={onClose} title={strings.staff.uploadImages} maxWidth="md">
      <>
        <DialogContentText sx={styles.subTitle}>
          <Typography variant="italic">{strings.staff.uploadImagesInfo}</Typography>
        </DialogContentText>
        <DialogContent sx={styles.contentList} dividers>
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <ImageDropzone
                onValidator={imageSizeValidator}
                onAcceptedFiles={onImageAdded}
                onRejectedFiles={onImageRejected}
                maxFiles={999}
                maxSize={5 * 1024 * 1024}
              />
            </Grid>
            <Grid item xs={6}>
              <h3>Krav på bild</h3>
              <ol>
                <li>Storlek/mått: Kvadrat - minst 800x800 px</li>
                <li>Frilagd på transparent bakgrund</li>
                <li>Friyta ovanför huvudet enligt exempel</li>
                <li>Personen i bild, från bröst till topp, se exempel</li>
                <li>Inga textelement eller annat än rena porträttbilder</li>
              </ol>
            </Grid>
            <Grid item xs={2}>
              <h3>Exempel</h3>
              <PlayerAvatar srcImage="/avatar.png" size="large" variant="square" showTransparency />
            </Grid>
            <Grid item xs={12}>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {imageMapping?.map(({ member, file, status, error }, i) => (
                      <TableRow key={i} sx={styles.tableRow}>
                        <TableCell style={{ width: '100px' }}>
                          <PlayerAvatar
                            srcImage={URL.createObjectURL(file)}
                            size="large"
                            showTransparency
                          />
                        </TableCell>
                        <TableCell>
                          {!status ? (
                            <StaffMemberSelect
                              staff={staff}
                              selectedMember={member}
                              onSelectMember={(selectedMember) => {
                                const newImageMapping = [...imageMapping];
                                newImageMapping[i].member = selectedMember;
                                setImageMapping(newImageMapping);
                              }}
                            />
                          ) : (
                            <Typography color="error" mt={2}>
                              {error}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell style={{ width: '100px' }}>
                          {status === 'uploading' && <CircularProgress />}
                          {status === 'uploaded' && <Check color="success" />}
                          {(!status || status === 'error') && (
                            <IconButton onClick={() => onRemoveImage(i)} color="error">
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </DialogContent>
      </>
      <DialogActions>
        <Button color="primary" onClick={onClose}>
          {strings.actions.close}
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          disabled={submitDisabled}
          loading={submitLoading}
          onClick={onSubmit}
        >
          {strings.actions.upload}
        </LoadingButton>
      </DialogActions>
    </SquadDialog>
  );
}
